import { useEffect, useState } from 'react';
import { LogoLoader, TwoFactorVerify } from '@gokrew/ui';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Form } from 'formik';
import { FormField, FormikWithSchema } from '@gokrew/ui/form';
import { FormikSubmitButton } from '@gokrew/ui/components/FormikSubmitButton';
import {
  clearSsoParams,
  decodeAccessTokenClaims,
  isSafeSsoReturnTo,
  isTerminalAuthError,
  readSsoRedirect,
  SsoButtons,
  SsoNotice,
  TerminalAuthError,
} from '@gokrew/ui/auth';
import { SSO_PROVIDER_LABEL } from '@gokrew/models/sso';
import toast from 'react-hot-toast';
import { useDialog } from '@gokrew/ui/context';
import { getApiErrorMessage } from '@gokrew/ui/utility/utility';
import { getConfig } from '@gokrew/ui/runtime-config';
import { useTranslation } from 'react-i18next';
import { EyeIcon, EyeOffIcon, ArrowRight } from 'lucide-react';
import { TranslationNS } from '@gokrew/models/constants';
import { login, resendOtp, verifyOtp } from '@/api/auth';
import type {
  LoginApiResponse,
  LoginForm,
  LoginResponse,
  OtpChallengeResponse,
} from '@gokrew/models/auth';
import { useMutation } from '@tanstack/react-query';
import { useEmployeePortalAuth } from '@/context/EmployeeAuthContext';
import { tokenManager } from '@/auth';
import type { AxiosError } from 'axios';
import { loginSchema } from '@/validation/authSchema';
import styles from './components/AuthContent.module.css';

// A plain `'requiresOtp' in data.data` check only narrows the `data`
// property itself, not the enclosing response — this type guard narrows
// the whole LoginApiResponse so callers can safely branch on it.
function isOtpChallengeResponse(
  response: LoginApiResponse
): response is OtpChallengeResponse {
  return 'requiresOtp' in response.data;
}

export function LoginPage() {
  const { t } = useTranslation(TranslationNS.Auth);
  const [showPassword, setShowPassword] = useState(false);
  const { setAuth, token } = useEmployeePortalAuth();
  const { openDialog } = useDialog();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');
  const [pendingAuth, setPendingAuth] = useState<{
    maskedEmail: string;
    otpToken: string;
  } | null>(null);
  const [lastEmail, setLastEmail] = useState('');
  const companyPortalUrl = getConfig().COMPANY_PORTAL_URL;
  const { t: tSso } = useTranslation(TranslationNS.Sso);
  // Google / Microsoft sign-in came back here (D199): `sso=complete` means
  // the callback set the refresh cookie, `sso_error` why it refused.
  const [sso] = useState(() => readSsoRedirect(window.location.search));
  const [ssoCompleting, setSsoCompleting] = useState(sso.complete);
  const [ssoError, setSsoError] = useState(
    sso.error
      ? { code: sso.error, provider: sso.provider, email: sso.email }
      : null
  );

  useEffect(() => {
    if (!sso.complete && !sso.error) return;
    clearSsoParams();
    if (!sso.complete) return;
    let cancelled = false;
    tokenManager.forceRefresh().then((accessToken) => {
      if (cancelled) return;
      if (!accessToken) {
        setSsoCompleting(false);
        setSsoError({ code: 'expired', provider: null, email: null });
        return;
      }
      if (sso.linked)
        toast.success(
          tSso('linked_toast', { provider: SSO_PROVIDER_LABEL[sso.linked] })
        );
      const claims = decodeAccessTokenClaims(accessToken);
      setAuth({
        message: '',
        data: {
          accessToken,
          sessionId: claims.sessionId ?? '',
          role: claims.role ?? '',
        },
      } satisfies LoginResponse);
    });
    return () => {
      cancelled = true;
    };
    // Runs once for the sign-in that landed on this page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loginMutation = useMutation<
    LoginApiResponse,
    AxiosError<{ message: string }>,
    LoginForm
  >({
    mutationFn: (payload: LoginForm) => login(payload),
    onSuccess: (data, payload) => {
      setLastEmail(payload.email);
      if (isOtpChallengeResponse(data)) {
        setPendingAuth({
          maskedEmail: data.data.maskedEmail,
          otpToken: data.data.otpToken,
        });
      } else {
        // 2FA disabled server-side (or the device is already trusted) —
        // tokens come back directly, no OTP step.
        setAuth(data);
      }
    },
    onError: (error) => {
      openDialog({
        title: t('login_failed'),
        content: (
          <p className="text-sm mt-2">
            {getApiErrorMessage(error, t('login_failed'))}
          </p>
        ),
      });
    },
  });

  // Post-login redirect: honor an explicit deep-link (e.g. session-expired
  // bounce), otherwise land on the dashboard. If the user lacks dashboard
  // permission, the route guard redirects them to a route they can access —
  // that check runs with fully-resolved permissions inside the auth layout.
  useEffect(() => {
    if (!token) return;
    navigate(redirect || '/dashboard', { replace: true });
  }, [token, redirect, navigate]);

  async function handleVerify(code: string): Promise<void> {
    if (!pendingAuth) return;
    try {
      const result = await verifyOtp(pendingAuth.otpToken, code);
      setAuth(result);
    } catch (err) {
      const message = getApiErrorMessage(err, t('invalid_code'));
      throw isTerminalAuthError(err)
        ? new TerminalAuthError(message)
        : new Error(message);
    }
  }

  async function handleResendCode(): Promise<void> {
    if (!pendingAuth) return;
    try {
      await resendOtp(pendingAuth.otpToken);
    } catch (err) {
      const message = getApiErrorMessage(err, t('login_failed'));
      throw isTerminalAuthError(err)
        ? new TerminalAuthError(message)
        : new Error(message);
    }
  }

  if (pendingAuth) {
    return (
      <TwoFactorVerify
        email={pendingAuth.maskedEmail}
        onVerify={handleVerify}
        onResend={handleResendCode}
        onBack={() => setPendingAuth(null)}
      />
    );
  }

  if (ssoCompleting) {
    return (
      <div className={styles.ssoCompleting}>
        <LogoLoader size="sm" />
        <p className={styles.subtext}>{tSso('completing')}</p>
      </div>
    );
  }

  return (
    <>
      <h2 className={styles.heading}>{t('welcome_back')}</h2>
      <p className={styles.subtext}>{t('signin_to_your_account')}</p>

      {ssoError && (
        <SsoNotice
          code={ssoError.code}
          provider={ssoError.provider}
          email={ssoError.email}
        />
      )}
      <SsoButtons
        portal="employee"
        returnTo={isSafeSsoReturnTo(redirect) ? redirect : null}
      />

      <FormikWithSchema
        initialValues={{
          email: lastEmail,
          password: '',
        }}
        onSubmit={(values: LoginForm, { setSubmitting }) => {
          loginMutation.mutate(values, {
            onSettled: () => setSubmitting(false),
          });
        }}
        validationSchema={loginSchema(t)}
      >
        {() => (
          <Form className={styles.form}>
            <FormField
              name="email"
              type="email"
              label={t('form.fields.email.label')}
              placeholder={t('form.fields.email.placeholder')}
            />

            <FormField
              name="password"
              type={showPassword ? 'text' : 'password'}
              label={t('form.fields.password.label')}
              placeholder={t('form.fields.password.placeholder')}
              IconSuffix={showPassword ? EyeIcon : EyeOffIcon}
              onIconClick={() => setShowPassword(!showPassword)}
            />

            <div className={styles.forgotRow}>
              <Link to="/forgot-password" className={styles.forgotLink}>
                {t('forgot_password')}
              </Link>
            </div>

            <FormikSubmitButton
              variant="primary"
              IconSuffix={loginMutation.isPending ? undefined : ArrowRight}
              disabled={loginMutation.isPending}
              className={styles.submitBtn}
            >
              {loginMutation.isPending ? <LogoLoader size="sm" /> : t('login')}
            </FormikSubmitButton>
          </Form>
        )}
      </FormikWithSchema>

      {/* Static on purpose: a failed sign-in never says which portal the
          account belongs to, so point HR admins to theirs up front. */}
      {companyPortalUrl && (
        <p className={styles.bottomRow}>
          {t('hr_portal_hint')}{' '}
          <a href={companyPortalUrl} className={styles.bottomLink}>
            {t('hr_portal_link')}
          </a>
        </p>
      )}
    </>
  );
}
